import * as React from "react";
import { Link } from "react-router";
import * as Popover from "@radix-ui/react-popover";
import { Check, Loader2 } from "lucide-react";

import { type NotificationTab, useMarkRead, useNotificationsFeed, useUnreadCount } from "@/api/notifications";
import { FilterChip } from "@/components/ui/filter-chip";
import { cn } from "@/lib/cn";
import { NotificationRow } from "./NotificationRow";

/**
 * ops/Bell (MV2-878) — the header bell's popover, 380 × 560: title + "N new"
 * + Mark all read, a chip row with unread counts (All · Mentions · Tickets ·
 * Alarms), the last eight rows, "View all notifications". Polls every 30 s
 * while open; keys 1–4 switch the chip.
 */

const TABS: ReadonlyArray<{ id: NotificationTab; label: string; shortcut: string; countKey: "all" | "mentions" | "tickets" | "alarms" }> = [
  { id: "all", label: "All", shortcut: "1", countKey: "all" },
  { id: "mentions", label: "Mentions", shortcut: "2", countKey: "mentions" },
  { id: "tickets", label: "Tickets", shortcut: "3", countKey: "tickets" },
  { id: "alarms", label: "Alarms", shortcut: "4", countKey: "alarms" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
}

export const NotificationsPopover: React.FC<Props> = ({ open, onOpenChange, trigger }) => {
  const [tab, setTab] = React.useState<NotificationTab>("all");
  const feed = useNotificationsFeed({ tab, limit: 8 }, { enabled: open, refetchInterval: open ? 30_000 : false });
  const unread = useUnreadCount();
  const markRead = useMarkRead();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const opt = TABS.find((t) => t.shortcut === e.key);
      if (opt) {
        e.preventDefault();
        setTab(opt.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const items = feed.data?.items ?? [];
  const unreadN = unread.data?.unread ?? 0;
  const counts = { all: unreadN, mentions: unread.data?.by_category.mentions ?? 0, tickets: unread.data?.by_category.tickets ?? 0, alarms: unread.data?.by_category.alarms ?? 0 };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className={cn(
            "z-50 flex max-h-[560px] w-[380px] flex-col overflow-hidden rounded-[10px] border border-gray-200 bg-surface-raised shadow-[0_12px_32px_rgba(2,6,23,.18)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none",
          )}
          role="dialog"
          aria-label="Notifications"
        >
          <div className="flex items-center gap-[9px] border-b border-gray-200 px-3.5 py-3">
            <span className="text-[14px] font-semibold text-gray-900">Notifications</span>
            {unreadN > 0 && (
              <span className="tabular inline-flex h-[18px] items-center rounded-[5px] px-[7px] text-[10.5px] font-semibold" style={{ background: "color-mix(in srgb, var(--action) 12%, transparent)", color: "var(--action)" }}>
                {unreadN > 99 ? "99+" : unreadN} new
              </span>
            )}
            <span className="flex-1" />
            <button type="button" onClick={() => markRead.mutate({ all: true })} disabled={unreadN === 0 || markRead.isPending} className="text-[11px] text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-45">
              Mark all read
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 px-3.5 py-[9px]" role="tablist" aria-label="Notification categories">
            {TABS.map((t) => (
              <FilterChip key={t.id} label={t.label} count={counts[t.countKey]} on={tab === t.id} onClick={() => setTab(t.id)} className="px-2.5 py-[3px] text-[11px]" />
            ))}
          </div>
          <div role="feed" className="flex flex-1 flex-col overflow-y-auto">
            {feed.isLoading ? (
              <div className="flex flex-1 items-center justify-center py-8 text-gray-400">
                <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden />
              </div>
            ) : feed.isError ? (
              <div className="px-3.5 py-6 text-center text-[12px] text-gray-500">
                Couldn't load notifications.{" "}
                <button type="button" onClick={() => void feed.refetch()} className="text-gray-900 underline">
                  Retry
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-1 px-3.5 py-10 text-center">
                <Check size={18} className="text-gray-400" aria-hidden />
                <p className="text-[12.5px] text-gray-500">Nothing to catch up on.</p>
              </div>
            ) : (
              items.map((row, idx) => (
                <NotificationRow
                  key={row.id}
                  row={row}
                  posInSet={idx + 1}
                  setSize={items.length}
                  onClick={() => {
                    onOpenChange(false);
                    if (row.read_at === null) markRead.mutate({ ids: [row.id] });
                  }}
                />
              ))
            )}
          </div>
          <div className="px-3.5 py-[11px]">
            <Link to="/notifications" onClick={() => onOpenChange(false)} className="text-[12.5px] text-[color:var(--action)] hover:underline">
              View all notifications
            </Link>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
